import React, { FC, ReactNode, createContext, useContext, useReducer } from 'react'
import {
  CustomerPreference,
  Employee,
  SensitiveEmployeeInfo,
  TicketRight,
  YearlyCategory,
  YearlyDisabilityProof,
  YearlyFee,
} from '../api-clients/api-types'

interface EmployeeContextInterface {
  employee: Employee
  dispatch: React.Dispatch<Action>
}

enum ActionState {
  UPDATE,
  UPDATE_SENSITIVE_INFO,
  UPDATE_YEARLY_FEE,
  UPDATE_YEARLY_DISABILITY_PROOF,
  UPDATE_YEARLY_CATEGORY,
  UPDATE_TICKET_RIGHTS,
  UPDATE_TICKET_RIGHT,
  UPDATE_CUSTOMER_PREFERENCES,
}

type Action =
  | { type: ActionState.UPDATE; employee: Employee }
  | { type: ActionState.UPDATE_SENSITIVE_INFO; sensitiveInfo: SensitiveEmployeeInfo }
  | { type: ActionState.UPDATE_YEARLY_FEE; yearlyFee: YearlyFee }
  | { type: ActionState.UPDATE_YEARLY_DISABILITY_PROOF; disabilityProof: YearlyDisabilityProof }
  | { type: ActionState.UPDATE_YEARLY_CATEGORY; category: YearlyCategory }
  | { type: ActionState.UPDATE_TICKET_RIGHTS; ticketRights: TicketRight[] }
  | { type: ActionState.UPDATE_TICKET_RIGHT; ticketRight: TicketRight }
  | { type: ActionState.UPDATE_CUSTOMER_PREFERENCES; customerPreferences: CustomerPreference }

const Reducer = (state: Employee, action: Action) => {
  switch (action.type) {
    case ActionState.UPDATE: {
      const ticketRights =
        action.employee.ticketRights !== null ? action.employee.ticketRights : state.ticketRights
      return { ...state, ...action.employee, ticketRights }
    }
    case ActionState.UPDATE_SENSITIVE_INFO:
      return { ...state, sensitiveInfo: action.sensitiveInfo }
    case ActionState.UPDATE_YEARLY_FEE: {
      const yearlyFees = state.yearlyFees?.filter((it) => it.year !== action.yearlyFee.year) || []
      yearlyFees.push(action.yearlyFee)
      return { ...state, yearlyFees }
    }
    case ActionState.UPDATE_YEARLY_DISABILITY_PROOF: {
      const disabilityProofs =
        state.sensitiveInfo?.disabilityProofs?.filter((it) => it.year !== action.disabilityProof.year) || []
      disabilityProofs.push(action.disabilityProof)
      const sensitiveInfo = {
        ...state.sensitiveInfo,
        disabilityProofs,
      }
      return { ...state, sensitiveInfo }
    }
    case ActionState.UPDATE_YEARLY_CATEGORY: {
      const categories = state.categories?.filter((it) => it.year !== action.category.year) || []
      categories.push(action.category)
      return { ...state, categories }
    }
    case ActionState.UPDATE_TICKET_RIGHTS: {
      return { ...state, ticketRights: action.ticketRights }
    }
    case ActionState.UPDATE_TICKET_RIGHT: {
      const ticketRights = state.ticketRights?.filter(
        (it) => it.ticketrightId !== action.ticketRight.ticketrightId
      )
      ticketRights?.push(action.ticketRight)
      return { ...state, ticketRights }
    }
    case ActionState.UPDATE_CUSTOMER_PREFERENCES: {
      return { ...state, customerPreferences: action.customerPreferences }
    }
    default:
      return state
  }
}

const EmployeeContext = createContext<EmployeeContextInterface>({} as EmployeeContextInterface)

export const useEmployee = () => {
  const context = useContext(EmployeeContext)
  return {
    employee: context.employee,
    setEmployee: (updatedEmployee: Employee) => {
      context?.dispatch({ type: ActionState.UPDATE, employee: updatedEmployee })
    },
    setSensitiveInfo: (sensitiveInfo: SensitiveEmployeeInfo) => {
      context?.dispatch({ type: ActionState.UPDATE_SENSITIVE_INFO, sensitiveInfo })
    },
    setYearlyFee: (yearlyFee: YearlyFee) => {
      context?.dispatch({ type: ActionState.UPDATE_YEARLY_FEE, yearlyFee })
    },
    setYearlyDisabilityProof: (disabilityProof: YearlyDisabilityProof) => {
      context?.dispatch({ type: ActionState.UPDATE_YEARLY_DISABILITY_PROOF, disabilityProof })
    },
    setYearlyCategories: (category: YearlyCategory) => {
      context?.dispatch({ type: ActionState.UPDATE_YEARLY_CATEGORY, category })
    },
    setTicketRights: (ticketRights: TicketRight[]) => {
      context?.dispatch({ type: ActionState.UPDATE_TICKET_RIGHTS, ticketRights })
    },
    setTicketRight: (ticketRight: TicketRight) => {
      context?.dispatch({ type: ActionState.UPDATE_TICKET_RIGHT, ticketRight })
    },
    setCustomerPreferences: (customerPreferences: CustomerPreference) => {
      context?.dispatch({ type: ActionState.UPDATE_CUSTOMER_PREFERENCES, customerPreferences })
    },
  }
}

interface EmployeeProviderProps {
  initEmployee: Employee
  children: ReactNode
}
export const EmployeeProvider: FC<EmployeeProviderProps> = ({ initEmployee, children }) => {
  const [employee, dispatch] = useReducer(Reducer, initEmployee)

  return <EmployeeContext.Provider value={{ employee, dispatch }}>{children}</EmployeeContext.Provider>
}
